import CommonCard from '../../../../global/card'
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import SOURCES from '../../../../../utils/constants'
import { useGenericQuery } from '../../../../../queries/use-generic-query'
import { QUERY_KEYS } from '../../../../../lib/query-constants'
import useDebounce from '../../../../../utils/customHooks'
import EnhancedPaymentHistoryList from './enhancedPaymentHistoryList'
import CommonSkeletonLoader from '../../../../global/loading'
import CommonPagination from '../../../../global/pagination'
import EnhancedPaymentHistoryCardView from './enhancedPaymentHistoryCardView'
import CommonCardView from '../../../../global/cardView'
import EnhancedPaymentHistoryDetails from './enhancedPaymentHistoryDetails'
import CommonDrawer from '../../../../global/drawer'
import NoRecordFound from '../../../../global/noRecordFound'

const EnhancedPaymentCustomerWise = () => {
    const [searchPaymentHistory, setSearchPaymentHistory] = useState('')
    const [paymentHistoryViewType, setPaymentHistoryViewType] = useState(() => {
        return localStorage.getItem("paymentHistoryViewType") || "GRID";
    });
    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });
    const [showDetails, setShowDetails] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
    const limitFromQuery = parseInt(
        searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
        24
    );
    const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);

    const [pagination, setPagination] = useState({
        offset: offsetFromQuery,
        limit: limitFromQuery,
        page: pageFromQuery,
    });

    const keyParams = useMemo(() => ({
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    }), [pagination.offset, pagination.limit, sort.sortType, sort.sortOrder]);

    const debouncedSearchTerm = useDebounce(searchPaymentHistory, 500);


    const { data, isLoading } = useGenericQuery({
        endpoint: "/admin/paymenthistory",
        queryKey: QUERY_KEYS.paymentHistory.lists({ ...keyParams }),
        queryParams: { ...keyParams },
        enabled: !debouncedSearchTerm,
        onSuccess: () => { },
        onError: () => { }
    })

    const { data: searchData, isInitialLoading: searchDataLoading } = useGenericQuery({
        endpoint: "/admin/paymenthistory/search",
        queryKey: QUERY_KEYS.paymentHistory.lists({ q: debouncedSearchTerm }),
        queryParams: { q: debouncedSearchTerm },
        onSuccess: () => { },
        onError: () => { },
        enabled: !!debouncedSearchTerm && debouncedSearchTerm.length >= 2
    })

    const isLoadingList = isLoading || searchDataLoading;

    const finalizedPaymentHistory = useMemo(() => {
        if (debouncedSearchTerm && searchData) {
            return searchData;
        }
        return data;
    }, [data, searchData, debouncedSearchTerm]);

    const finalizedData = useMemo(() => {
        if (!finalizedPaymentHistory || !finalizedPaymentHistory.payments) return [];
        return finalizedPaymentHistory.payments.map((item, idx) => ({
            ...item,
            key: item._id || idx,
            id: item._id || idx,
        }));
    }, [finalizedPaymentHistory]);

    const handleViewDetails = (record) => {
        setSelectedPayment(record);
        setShowDetails(true);
    };

    return (
        <CommonCard
            isSearch={{
                show: true,
                placeholder: "Search payment history.",
                searchInput: searchPaymentHistory,
                setSearchInput: setSearchPaymentHistory
            }}
            isListGridView={{
                show: true,
                setViewType: setPaymentHistoryViewType,
                viewType: paymentHistoryViewType,
                viewFor: "paymentHistoryViewType"
            }}
            isLoading={{
                show: isLoadingList,
                loaderCmp: <CommonSkeletonLoader type={paymentHistoryViewType === "GRID" ? "card" : "table"} />
            }}
        >
            {!isLoadingList && (!finalizedData || finalizedData.length === 0) ? (
                <NoRecordFound message="No payment history found." />
            ) : (
                <>
                    {paymentHistoryViewType === "LIST" ? (
                        <EnhancedPaymentHistoryList
                            paymentHistory={finalizedPaymentHistory}
                            sort={sort}
                            setSort={setSort}
                            isLoading={isLoadingList}
                            handleViewDetails={handleViewDetails}
                        />
                    ) : (
                        <CommonCardView>
                            {finalizedData.map((record) => (
                                <EnhancedPaymentHistoryCardView
                                    key={record.id}
                                    record={record}
                                    onCardClick={handleViewDetails}
                                />
                            ))}
                        </CommonCardView>
                    )}
                </>
            )}

            {
                !debouncedSearchTerm &&
                finalizedPaymentHistory?.totalRecords > SOURCES.DEFAULT_RECORDS_LISTING && (
                    <CommonPagination
                        currentPage={pagination.page}
                        defaultLimit={pagination.limit}
                        totalRecords={finalizedPaymentHistory?.totalRecords}
                        setCurrentPage={setPagination}
                        activePageRecords={finalizedPaymentHistory?.payments?.length || 0}
                        paginationFor="Payment History"
                        className="mt-4"
                    />
                )
            }

            {showDetails && (
                <CommonDrawer
                    open={showDetails}
                    setOpen={() => setShowDetails(false)}
                    title="Payment Details"
                    width="1000px"
                    children={
                        <EnhancedPaymentHistoryDetails
                            paymentDetail={selectedPayment}
                        />
                    }
                />
            )}
        </CommonCard>
    )
}

export default EnhancedPaymentCustomerWise